import React, { useState } from 'react'
import Button from 'uiComponents/buttons'
import { DisabledItemHoverInfotip } from 'uiComponents/infotip'
import { Modal, ModalBody, ModalFooter } from 'uiComponents/popups/modal.new'
import { LucideIcon } from 'utils/lucideIcon'
import { useCopyToClipboard } from '../../hooks/useCopyToClipboard'
import { ServiceAppWithSecret } from '../../schema'
import './secretRevealModal.scss'
import { useTranslation } from 'translations'

interface SecretRevealModalProps {
    app: ServiceAppWithSecret
    reason: 'created' | 'rotated'
    onDone: () => void
}

const CopyableValue: React.FC<{ label: string; value: string; secret?: boolean }> = ({ label, value, secret }) => {
    const { copied, copy } = useCopyToClipboard()
    const { t } = useTranslation()

    return (
        <div className="service-app-secret-field">
            <span className="service-app-secret-field-label">{label}</span>
            <div className="service-app-secret-field-row">
                <code className={secret ? 'service-app-secret-value is-secret' : 'service-app-secret-value'}>
                    {value}
                </code>
                <button type="button" className="service-app-copy-button" onClick={() => copy(value)}>
                    <LucideIcon iconName={copied ? 'Check' : 'Copy'} size={14} />
                    {copied
                        ? t('settings.personalSettings.serviceApps.secretModal.copied')
                        : t('settings.personalSettings.serviceApps.secretModal.copy')}
                </button>
            </div>
        </div>
    )
}

export const SecretRevealModal: React.FC<SecretRevealModalProps> = ({ app, reason, onDone }) => {
    const [acknowledged, setAcknowledged] = useState(false)
    const { t } = useTranslation()

    return (
        <Modal className="service-app-secret-modal">
            <ModalBody>
                <span className="service-app-secret-eyebrow">
                    <LucideIcon iconName="ShieldCheck" size={14} />
                    {t('settings.personalSettings.serviceApps.secretModal.eyebrow')}
                </span>
                <h4 className="service-app-secret-heading">
                    {reason === 'created'
                        ? t('settings.personalSettings.serviceApps.secretModal.headingCreated')
                        : t('settings.personalSettings.serviceApps.secretModal.headingRotated')}
                </h4>
                <p className="service-app-secret-intro">
                    {reason === 'rotated'
                        ? t('settings.personalSettings.serviceApps.secretModal.introRotated')
                        : t('settings.personalSettings.serviceApps.secretModal.intro')}
                </p>

                <CopyableValue
                    label={t('settings.personalSettings.serviceApps.secretModal.clientId')}
                    value={app.clientId}
                />
                <CopyableValue
                    label={t('settings.personalSettings.serviceApps.secretModal.clientSecret')}
                    value={app.clientSecret}
                    secret
                />

                <div className="service-app-secret-warning">
                    <LucideIcon iconName="TriangleAlert" size={16} />
                    <span>{t('settings.personalSettings.serviceApps.secretModal.warning')}</span>
                </div>

                <label className="service-app-secret-confirm">
                    <input
                        type="checkbox"
                        checked={acknowledged}
                        onChange={(e) => setAcknowledged(e.target.checked)}
                    />
                    <span>{t('settings.personalSettings.serviceApps.secretModal.acknowledge')}</span>
                </label>
            </ModalBody>
            <ModalFooter>
                <DisabledItemHoverInfotip
                    active={!acknowledged}
                    infotipText={t('settings.personalSettings.serviceApps.secretModal.acknowledgeRequired')}
                    width="18rem"
                    toTheLeft
                >
                    <Button kind="action" disabled={!acknowledged} onClick={onDone}>
                        {t('settings.personalSettings.serviceApps.secretModal.done')}
                    </Button>
                </DisabledItemHoverInfotip>
            </ModalFooter>
        </Modal>
    )
}
